import * as React from "react";
import { IMobileAccess } from "@nju/data-access-portal-product";
import { IMnpPortoutTask } from "@nju/data-access-portal-service";
import { IconName } from "@nju/icons";
import { BlockText } from "@nju/ui";
import {
  differenceInCalendarDays,
  formatDate,
  formatPhoneNumber,
  POSITIVE_DIGITS,
} from "@nju/utils";
import { IMobileServiceCTABannerConditions } from "./types";

interface IStatusProps {
  title: string | React.ReactNode;
  text: string | React.ReactNode;
  iconName: IconName;
}

interface IGetStatusProps {
  conditions: IMobileServiceCTABannerConditions;
  portoutData?: IMnpPortoutTask["properties"] | undefined;
  mobileAccess?: IMobileAccess | undefined;
}

export function getMobileStatusData({
  conditions,
  portoutData,
  mobileAccess,
}: IGetStatusProps): IStatusProps | undefined {
  const {
    isPendingCancellation,
    isPortoutCancellationPossible,
    isPortoutCancellationNotPossible,
    isPortoutCancellationCheckInProgress,
    isNoPayment,
    isSuspended,
    isPendingReactivation,
    isPendingTermination,
    isUnsubscribed,
    isDisconnected,
  } = conditions;

  if (isPendingCancellation) {
    return {
      title: "w trakcie odstąpienia",
      text: "Dziękujemy, że byłeś/byłaś z nami. Twoja usługa nie jest już aktywna.",
      iconName: "info-circle",
    };
  }

  if (isPendingTermination) {
    return {
      title: "usługa w trakcie wyłączenia",
      text: "Już za chwilę całkowicie odłączymy Twoją usługę, a Ty stracisz swój obecny numer.",
      iconName: "times-circle",
    };
  }

  if (isPortoutCancellationPossible && portoutData) {
    return {
      title: (
        <BlockText
          size={{ "@onlyMobile": "m", "@fromTablet": "xl" }}
          weight="bold"
        >
          przenosisz numer w dniu {formatDate(portoutData.npDueDate, false)}
        </BlockText>
      ),
      text: (
        <>
          <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
            {portoutData.npOperator} poinformował nas, że chcesz do niego
            przenieść numer{" "}
            {formatPhoneNumber(
              portoutData.npMSISDN,
              POSITIVE_DIGITS,
              " ",
              "",
              true,
            )}
            .
          </BlockText>
          <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
            Pamiętaj, jeśli zmienisz zdanie, możesz anulować przeniesienie
            numeru i zostać w nju subskrypcja. Możesz to zrobić najpóźniej 2 dni
            przed potwierdzoną datą przeniesienia.
          </BlockText>
        </>
      ),
      iconName: "info-circle",
    };
  }

  if (isNoPayment && mobileAccess?.statusChangeDate) {
    const time = formatDate(mobileAccess.statusChangeDate);

    return {
      title: "brak płatności za usługę",
      text: `${time} nie udało nam się pobrać płatności. Jeśli w ciągu 24 godzin nie otrzymamy płatności, Twoja usługa zostanie wyłączona.`,
      iconName: "times-circle",
    };
  }

  if (
    isSuspended &&
    mobileAccess?.statusChangeDate &&
    mobileAccess?.terminationDate
  ) {
    const time = formatDate(mobileAccess.statusChangeDate, false);
    const terminationDate = formatDate(mobileAccess.terminationDate, false);

    return {
      title: "usługa została wyłączona",
      text: `${time} usługa nju subskrypcja została wyłączona ze względu na brak płatności. Możesz ją ponownie włączyć, gdy opłacisz usługę do ${terminationDate}. Po tej dacie stracisz numer.`,
      iconName: "exclamation-circle",
    };
  }

  if (
    isSuspended &&
    mobileAccess?.statusChangeDate &&
    !mobileAccess?.terminationDate
  ) {
    const time = formatDate(mobileAccess.statusChangeDate, false);

    return {
      title: "usługa została wyłączona",
      text: `${time} usługa nju subskrypcja została wyłączona ze względu na brak płatności. Możesz ją ponownie włączyć, gdy opłacisz usługę.`,
      iconName: "exclamation-circle",
    };
  }

  if (isUnsubscribed && mobileAccess?.anniversaryDate) {
    const time = formatDate(mobileAccess.anniversaryDate, false);
    const diffDays = differenceInCalendarDays(mobileAccess.anniversaryDate);

    return {
      title: `usługa zostanie zakończona za ${diffDays} dni (${time})`,
      text: `Twoja usługa nju subskrypcja będzie aktywna do ${time}.`,
      iconName: "exclamation-circle",
    };
  }

  if (
    isDisconnected &&
    mobileAccess?.statusChangeDate &&
    mobileAccess?.terminationDate
  ) {
    const time = formatDate(mobileAccess.statusChangeDate, false);
    const terminationDate = formatDate(mobileAccess.terminationDate, false);

    return {
      title: "usługa została wyłączona",
      text: `${time} usługa nju subskrypcja została wyłączona. Możesz ją ponownie włączyć, gdy opłacisz usługę do ${terminationDate}. Po tej dacie stracisz numer`,
      iconName: "info-circle",
    };
  }

  if (isPendingReactivation) {
    return {
      title: "usługa w trakcie włączania",
      text: `Trwa ponowna aktywacja Twojej usługi.`,
      iconName: "spinner",
    };
  }

  if (isPortoutCancellationNotPossible && portoutData) {
    return {
      title: (
        <BlockText
          size={{ "@onlyMobile": "m", "@fromTablet": "xl" }}
          weight="bold"
        >
          przenosisz numer w dniu {formatDate(portoutData.npDueDate, false)}
        </BlockText>
      ),
      text: (
        <>
          <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
            Twój numer{" "}
            {formatPhoneNumber(
              portoutData.npMSISDN,
              POSITIVE_DIGITS,
              " ",
              "+48",
              true,
            )}{" "}
            zostanie przeniesiony do {portoutData.npOperator}
          </BlockText>
        </>
      ),
      iconName: "info-circle",
    };
  }

  if (isPortoutCancellationCheckInProgress) {
    return {
      title: "sprawdzamy, czy możemy anulować przeniesienie numeru",
      text: "Próbujemy anulować przeniesienie numeru do innego operatora. Poinformujemy Cię, gdy będzie to możliwe.",
      iconName: "info-circle",
    };
  }

  return undefined;
}
